import { REFUSAL_COPY } from "../constants";
import type { TurnOutcome } from "../types";
import HsCandidates from "./HsCandidates";
import MarkdownAnswer from "./MarkdownAnswer";

interface Props {
  outcome: TurnOutcome;
  onRetry?: () => void;
  onAnswerField?: (value: string) => void;
  disabled?: boolean;
}

const TurnOutcomeView = ({ outcome, onRetry, onAnswerField, disabled }: Props) => {
  if (outcome.kind === "answer") {
    const candidates = outcome.event.hsCandidates;
    return (
      <div>
        <MarkdownAnswer text={outcome.event.text} />
        {candidates?.length ? <HsCandidates groups={candidates} onSubmit={(value) => onAnswerField?.(value)} disabled={disabled} /> : null}
      </div>
    );
  }

  if (outcome.kind === "needs_input") {
    return (
      <div className="tw-rounded-xl tw-border tw-border-[#DFE2FC] tw-bg-[#FAFAFE] tw-p-3">
        <div className="tw-mb-3 tw-text-label-md tw-leading-relaxed tw-text-[#25233D]">{outcome.event.prompt}</div>

        {outcome.event.fields.map((field) => (
          <div key={field.field} className="tw-mb-3 last:tw-mb-0">
            <div className="tw-text-label-sm tw-font-medium tw-text-[#1E1B4B]">{field.whyNeeded}</div>
            {field.howToObtain && <div className="tw-mt-1 tw-text-label-xs tw-leading-relaxed tw-text-gray-500">{field.howToObtain}</div>}

            {field.options?.length ? (
              <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-1.5">
                {field.options.map((option) => (
                  <button
                    key={option}
                    type="button"
                    disabled={disabled}
                    onClick={() => onAnswerField?.(option)}
                    className="tw-rounded-lg tw-border tw-border-[#DFE2FC] tw-bg-white tw-px-3 tw-py-2 tw-text-left tw-text-label-sm tw-text-[#3B3960] hover:tw-bg-[#EEF0FE] disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                  >
                    {option}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    );
  }

  const copy = REFUSAL_COPY[outcome.event.reason];
  const warning = copy.tone === "warning";

  return (
    <div
      className={`tw-rounded-xl tw-border tw-p-3 ${warning ? "tw-border-warning-200 tw-bg-warning-50" : "tw-border-gray-200 tw-bg-gray-50"}`}
    >
      <div className={`tw-text-label-md tw-font-semibold ${warning ? "tw-text-[#92610A]" : "tw-text-gray-700"}`}>{copy.title}</div>

      <div className="tw-mt-1.5 tw-text-label-sm tw-leading-relaxed tw-text-[#25233D]">{outcome.event.text}</div>

      <div className="tw-mt-2 tw-text-label-sm tw-leading-relaxed tw-text-gray-600">{copy.action}</div>

      {copy.canRetry && onRetry && (
        <button
          type="button"
          disabled={disabled}
          onClick={onRetry}
          className="tw-mt-3 tw-rounded-lg tw-border tw-border-[#DFE2FC] tw-bg-white tw-px-3 tw-py-1.5 tw-text-label-sm tw-font-medium tw-text-[#4F46E5] hover:tw-bg-[#EEF0FE] disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
        >
          Try again
        </button>
      )}
    </div>
  );
};

export default TurnOutcomeView;
